import { environment } from 'src/environments/environment';

// Content of the render flow (the landing's own copy is in render-v2.data.ts). It began as the
// boss's prototype (https://3dway-render.fridlyand.com/, docs/render-landing-port.md) with one
// product change the prototype got wrong: the free preview is a still frame in both orientations,
// not watermarked videos. Videos exist only after the $9 purchase.

// What the render pipeline produces, the files the pack hands out (render_download): 1920 × 1080 and
// 1080 × 1920, 60 fps, since the backend switched the renderer to them (owner, 2026-11-02; the
// renderer takes the heights from the backend's VERTICAL_RESOLUTION_WEB / _APP), H.264 (owner,
// 2026-11-03; it was HEVC). Before that 1920 × 1038 and 856 × 1852 (ffprobe, a dev job of
// 2026-10-23): the models rendered until then, and
// the samples' own files under /_render-hq, are that size. The length is the renders' since 2026-10:
// 720 frames at 60 fps, 12 seconds (designer, 2026-10-23; it was 15 — 900 frames). Size estimates
// read it from here; no text on the pages names it (owner, 2026-11-05).
export const RENDER_OUTPUT_SPEC = {
  landscape: { width: 1920, height: 1080 },
  portrait: { width: 1080, height: 1920 },
  seconds: 12,
  fps: 60,
  codec: 'H.264',
  container: 'MP4',
} as const;

export const RENDER_PRICE_USD = 9;
// What the $9 buys, on the preview's order card and a sample's card.
export const RENDER_OFFER_BENEFITS: ReadonlyArray<string> = ['Landscape and portrait videos', 'Versions for every platform included', 'Download again without paying again'];
// Where a stuck payment or a failed render is taken up (the site footer's address).
export const RENDER_SUPPORT_EMAIL = 'support@3dway.io';

// Model formats the backend takes (AVAILABLE_MODELS_EXTENSIONS=usdz,fbx,usd in its .env), and the
// texture files that may travel with them in a folder. The library's ALLOWED_MODELS_EXTENSIONS also
// lists .usda/.usdc, which the backend does not: refused here, before anything is uploaded.
export const RENDER_MODEL_EXTENSIONS = ['.fbx', '.usdz', '.usd'];
export const RENDER_TEXTURE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.tga', '.tif', '.tiff', '.exr', '.bmp', '.webp', '.hdr', '.psd'];
// Whole upload (model + textures). Nobody has set a real limit yet — open question in the doc.
export const RENDER_UPLOAD_LIMIT_BYTES = 500 * 1024 * 1024;

export type RenderSampleId = 'car' | 'drone' | 'samurai';

export interface RenderSample {
  id: RenderSampleId;
  name: string;
  artist: string;
  location: string;
  // The public model page on 3dway.io (the credit link).
  modelUrl: string;
  // The file name the upload step shows under the sample's thumbnail (the designer's naming).
  fileName: string;
  // What the sample's played upload counts up to — a plausible size for such a model, not the
  // real file's (render-drag.ts).
  fileBytes: number;
  stills: RenderStills;
  videos: RenderVideos;
}

export interface RenderStills {
  landscape: string;
  portrait: string;
  // 960/465 px copies for cards and thumbnails.
  landscapeCard: string;
  portraitCard: string;
  // Which frames are still the preview's, with its watermark burned in (the render backend's
  // preview-only stills): a paid model shows those blurred until its clean ones come
  // (renderFrameVeiled). Absent: both clean (the samples, the mock).
  watermarked?: { landscape: boolean; portrait: boolean };
}

export interface RenderVideos {
  landscape: string;
  portrait: string;
}

const STILLS = 'assets/images/render';
// Real 360° renders the classic landing plays too; HEVC like everything under /_landing.
const VIDEOS = `${environment.landingMediaBaseUrl}/_landing`;

// `ext`: 'jpg' for stills cut from the renderer's lossless frames (prod spot_frames, JPEG q90 at the
// renderer's size then, 1920 × 1038 / 856 × 1852); the WebPs are frames of the 0.8–2 Mbps videos, soft
// and with the models' decals gone (designer's complaint, 2026-10-23).
function stills(id: RenderSampleId, ext: 'webp' | 'jpg' = 'webp'): RenderStills {
  return {
    landscape: `${STILLS}/${id}-landscape.${ext}`,
    portrait: `${STILLS}/${id}-portrait.${ext}`,
    landscapeCard: `${STILLS}/${id}-landscape-card.${ext}`,
    portraitCard: `${STILLS}/${id}-portrait-card.${ext}`,
  };
}

function videos(index: number): RenderVideos {
  return {
    landscape: `${VIDEOS}/${index}_web_hevc.mp4`,
    portrait: `${VIDEOS}/${index}_mob_hevc.mp4`,
  };
}

// A render with no /_landing copy: the pipeline's own files, copied to public storage /_render-hq
// (HEVC about 2 Mbps, 1920 × 1038 and 856 × 1852: the renderer's size until 2026-11-02).
function hqVideos(folder: string): RenderVideos {
  const base = `${environment.landingMediaBaseUrl}/_render-hq/${folder}/${folder}`;
  return { landscape: `${base}_web.mp4`, portrait: `${base}_app.mp4` };
}

const megabytes = (value: number): number => Math.round(value * 1024 * 1024);

// The drone's render as the pipeline named it (spaces made underscores on public storage).
export const RENDER_DRONE_FOLDER = 'Sci-Fi_Vehicle_Series-Drone_11792097895287';

// The samples of the upload step and the landing's bands: our own renders, with their authors.
export const RENDER_SAMPLES: ReadonlyArray<RenderSample> = [
  {
    id: 'car',
    name: 'Lamborghini',
    artist: 'Bảo Toàn',
    location: 'Vietnam',
    modelUrl: 'https://www.3dway.io/model/JwlZYv5FL4o5um7FBTiEg8',
    fileName: 'Lamborghini.fbx',
    fileBytes: megabytes(48.3),
    stills: stills('car'),
    videos: videos(5),
  },
  // The designer's pick in place of the Collier Flintlock Revolver (2026-10-16), the landing's first
  // band too: REX Hsu's "Sci-Fi Vehicle Series-Drone 03" (ArtStation rex2017, New Taipei), on 3dway
  // as "Drone 1".
  {
    id: 'drone',
    name: 'Sci-Fi Vehicle Series Drone 03',
    artist: 'REX Hsu',
    location: 'Taiwan',
    modelUrl: 'https://www.3dway.io/model/FA5eCUQfrpyXLoWMSEQCWQ',
    fileName: 'Sci-Fi_Vehicle_Series-Drone_03.fbx',
    fileBytes: megabytes(64.8),
    stills: stills('drone', 'jpg'),
    videos: hqVideos(RENDER_DRONE_FOLDER),
  },
  {
    id: 'samurai',
    name: 'Cyber Samurai',
    artist: 'Khoa Nguyen Minh Duong',
    location: 'Vietnam',
    modelUrl: 'https://www.3dway.io/model/dBJusCw8WEYrBSJTYGdL78',
    fileName: 'Cyber_Samurai.usdz',
    fileBytes: megabytes(61.2),
    stills: stills('samurai'),
    videos: videos(2),
  },
];

export const RENDER_HERO_SAMPLE: RenderSampleId = 'car';

export function renderSampleById(id: string | null | undefined): RenderSample | undefined {
  return RENDER_SAMPLES.find((sample) => sample.id === id);
}

// A link that saves the file instead of opening it. The sample videos sit on public storage (GCS),
// which honours response-content-disposition on a plain object URL; the browser ignores the
// download attribute on a cross-origin link, so this is what makes the button download.
export function renderAttachmentUrl(url: string, fileName: string): string {
  const safeName = fileName.replace(/["\\\r\n]/g, '');
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}response-content-disposition=${encodeURIComponent(`attachment; filename="${safeName}"`)}`;
}

// A model's name as the base of its files' names (the downloads page's "File name", designer +
// owner, 2026-10-16): what Windows and macOS refuse in a file name taken out, spaces run together,
// 80 characters at most. Empty when nothing usable is left.
export function renderFileStem(name: string): string {
  // eslint-disable-next-line no-control-regex
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80).trim();
}

// "Drone 03 landscape.mp4": one video of the pack as it is saved.
export function renderVideoFileName(name: string, orientation: RenderOrientation): string {
  return `${renderFileStem(name) || 'video'} ${orientation}.mp4`;
}

// "1920 × 1080" etc. for the spec lines.
export function renderDimensions(orientation: RenderOrientation): string {
  const { width, height } = RENDER_OUTPUT_SPEC[orientation];
  return `${width} × ${height}`;
}

export type RenderOrientation = 'landscape' | 'portrait';

// --- "Optimize for platforms" (designer, Figma 28563:27840, 2026-09-24) ---
// The downloads page offers the videos as the platforms want them, both orientations, packed into
// one ZIP. Her six platforms need only three encodes besides the master (official upload specs,
// checked 2026-09-24 — sources in docs/render-landing-port.md): every one of them takes H.264
// 1080p at 60 fps; the vertical feeds want 9:16, which our portrait is since 2026-11-02 (1080 × 1920;
// the 856 × 1852 before it was taller, and Instagram crops anything taller); ArtStation caps a free
// account's clip at 25 MB. Platforms whose files coincide share a row, joined by a comma — as she
// asked. The mock's sizes are estimates, bitrate × 12 s; the master's bitrate is an assumption. The dev render backend's pack (render_download,
// Yahor 2026-11-05) holds each ticked cell's own file, named for its platform ("Drone 03 ArtStation
// portrait.mp4"): the renderer's export, or the plain video for a model rendered before the exports.
// Its sizes are those files' (render_download_info).

export interface RenderPackEncode {
  // One file: cells that share an encode (YouTube's portrait and Instagram's) get it once.
  id: string;
  width: number;
  height: number;
  codec: 'H.265' | 'H.264';
  mbps: number;
  // Landscape set in a vertical 9:16 frame for the phone feeds — her rotate icon.
  verticalFrame?: boolean;
}

export interface RenderPackPreset {
  id: string;
  label: string;
  // The platform as render_download takes it, and the word its files carry there; the source's
  // carry none.
  platform: 'source' | 'instagram_tiktok' | 'artstation' | 'youtube_linkedin';
  fileLabel: string;
  portrait: RenderPackEncode;
  landscape: RenderPackEncode;
}

const MASTER_LANDSCAPE: RenderPackEncode = { id: 'master-landscape', ...RENDER_OUTPUT_SPEC.landscape, codec: RENDER_OUTPUT_SPEC.codec, mbps: 16 };
const MASTER_PORTRAIT: RenderPackEncode = { id: 'master-portrait', ...RENDER_OUTPUT_SPEC.portrait, codec: RENDER_OUTPUT_SPEC.codec, mbps: 14 };
// H.264 High, 12 Mbps: YouTube's own figure for 1080p at 48–60 fps, inside Instagram's 25 Mbps cap.
const VERTICAL_FEED: RenderPackEncode = { id: 'vertical-9x16', width: 1080, height: 1920, codec: 'H.264', mbps: 12 };
const VERTICAL_FEED_LANDSCAPE: RenderPackEncode = { ...VERTICAL_FEED, id: 'vertical-9x16-landscape', verticalFrame: true };
const WIDE_FEED: RenderPackEncode = { id: 'wide-16x9', width: 1920, height: 1080, codec: 'H.264', mbps: 12 };
// Under ArtStation's 25 MB (free accounts; a December 2025 note says 15 MB) at 12 s.
const ARTSTATION_LANDSCAPE: RenderPackEncode = { id: 'artstation-landscape', width: 1920, height: 1080, codec: 'H.264', mbps: 7 };
const ARTSTATION_PORTRAIT: RenderPackEncode = { id: 'artstation-portrait', ...RENDER_OUTPUT_SPEC.portrait, codec: 'H.264', mbps: 7 };

// Her order (Figma 28565:28098): ArtStation between the two-line rows, which ran together on a
// phone (designer, 2026-10-01).
export const RENDER_PACK_PRESETS: ReadonlyArray<RenderPackPreset> = [
  { id: 'source', label: 'Source file', platform: 'source', fileLabel: '', portrait: MASTER_PORTRAIT, landscape: MASTER_LANDSCAPE },
  {
    id: 'instagram-tiktok',
    label: 'Instagram, TikTok',
    platform: 'instagram_tiktok',
    fileLabel: 'Instagram-TikTok',
    portrait: VERTICAL_FEED,
    landscape: VERTICAL_FEED_LANDSCAPE,
  },
  { id: 'artstation', label: 'ArtStation', platform: 'artstation', fileLabel: 'ArtStation', portrait: ARTSTATION_PORTRAIT, landscape: ARTSTATION_LANDSCAPE },
  { id: 'youtube-linkedin', label: 'YouTube, LinkedIn', platform: 'youtube_linkedin', fileLabel: 'YouTube-LinkedIn', portrait: VERTICAL_FEED, landscape: WIDE_FEED },
];

// A cell of the table: "<preset>.<orientation>".
export type RenderPackKey = string;

export function renderPackKey(presetId: string, orientation: RenderOrientation): RenderPackKey {
  return `${presetId}.${orientation}`;
}

export const RENDER_PACK_KEYS: ReadonlyArray<RenderPackKey> = RENDER_PACK_PRESETS.reduce<RenderPackKey[]>(
  (keys, preset) => [...keys, renderPackKey(preset.id, 'portrait'), renderPackKey(preset.id, 'landscape')],
  [],
);

// Until the user saves a configuration: the two videos as rendered.
export const RENDER_PACK_DEFAULT: ReadonlyArray<RenderPackKey> = [renderPackKey('source', 'portrait'), renderPackKey('source', 'landscape')];

export function renderPackEncode(key: RenderPackKey): RenderPackEncode | undefined {
  const cell = renderPackCell(key);
  return cell && cell.preset[cell.orientation];
}

// A cell's row and column.
export function renderPackCell(key: RenderPackKey): { preset: RenderPackPreset; orientation: RenderOrientation } | undefined {
  const [presetId, orientation] = key.split('.') as [string, RenderOrientation];
  const preset = RENDER_PACK_PRESETS.find((item) => item.id === presetId);
  return preset && (orientation === 'portrait' || orientation === 'landscape') ? { preset, orientation } : undefined;
}

// "Drone 03 Instagram-TikTok portrait.mp4": a cell's file in the backend's pack, named as
// render_download names it ("{name} {label} {orientation}.mp4", no label for the source).
export function renderPackFileName(name: string, key: RenderPackKey): string {
  const cell = renderPackCell(key);
  if (!cell) {
    return '';
  }
  return [renderFileStem(name) || 'video', cell.preset.fileLabel, `${cell.orientation}.mp4`].filter(Boolean).join(' ');
}

// Bytes of an encode over the render's length.
export function renderPackBytes(encode: RenderPackEncode): number {
  return (encode.mbps * 1e6 * RENDER_OUTPUT_SPEC.seconds) / 8;
}

// The pack's size: every distinct file once.
export function renderPackTotalBytes(keys: ReadonlyArray<RenderPackKey>): number {
  const encodes = new Map<string, RenderPackEncode>();
  keys.forEach((key) => {
    const encode = renderPackEncode(key);
    if (encode) {
      encodes.set(encode.id, encode);
    }
  });
  return [...encodes.values()].reduce((total, encode) => total + renderPackBytes(encode), 0);
}

// The files of one platform as render_download_info lists them.
export type RenderPackInfoFiles = Partial<Record<string, Partial<Record<RenderOrientation, { size_bytes?: number }>>>>;

// Each cell's size from render_download_info, or null if it does not give them all. The info lists
// YouTube and LinkedIn apart; the row they share downloads `youtube_linkedin`, the backend's alias of
// YouTube's file.
export function renderPackCellSizes(files: RenderPackInfoFiles | undefined): Record<RenderPackKey, number> | null {
  const sizes: Record<RenderPackKey, number> = {};
  for (const key of RENDER_PACK_KEYS) {
    const cell = renderPackCell(key);
    const platform = cell?.preset.platform === 'youtube_linkedin' ? 'youtube' : cell?.preset.platform;
    const bytes = cell && platform ? Number(files?.[platform]?.[cell.orientation]?.size_bytes) : 0;
    if (!bytes) {
      return null;
    }
    sizes[key] = bytes;
  }
  return sizes;
}

// "23MB", "1.2GB" — the designer's form, no space. Under 10MB a decimal, under 1MB KB: a short render
// weighs that little, and every cell read "1MB" (owner, 2026-11-05).
export function renderPackSize(bytes: number): string {
  if (bytes >= 1e9) {
    return `${Math.round(bytes / 1e8) / 10}GB`;
  }
  if (bytes >= 1e7) {
    return `${Math.round(bytes / 1e6)}MB`;
  }
  if (bytes >= 999500) {
    return `${Math.round(bytes / 1e5) / 10}MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1e3))}KB`;
}

// "1080 × 1920 · H.264 · 60 fps" for a cell's tooltip.
export function renderPackSpec(encode: RenderPackEncode): string {
  return `${encode.width} × ${encode.height} · ${encode.codec} · ${RENDER_OUTPUT_SPEC.fps} fps`;
}

// "60 fps · MP4" — the designer's order. In parts, so a narrow line breaks between them (designer,
// 2026-09-25).
export const RENDER_VIDEO_SPEC_PARTS: ReadonlyArray<string> = [`${RENDER_OUTPUT_SPEC.fps} fps`, RENDER_OUTPUT_SPEC.container];

// The preview's bar, 0–100, from the upload's first byte to the frames (owner, 2026-10-20, decision
// 41): the upload fills the first tenth by its bytes; each stage after it starts where `from` says
// and fills its share at the pace it usually takes (`expectedMs`), creeping on while the renderer
// reports nothing, never past the next stage's start before the renderer says so, never back.
export const RENDER_UPLOAD_BAR = 10;
// One of the two frames saved: the rendering stage's floor from then on.
export const RENDER_PREVIEW_ONE_FRAME = 75;

// The preview's stages after the upload as the processing card lists them. The mock backend walks
// through them; the live one reports where it stands. Each tick is a signal the renderer really
// sends (owner, 2026-10-20, decision 40): the studio is ready when a GPU job has started
// (`podStarted`), the model is checked when the renderer has opened it and rendered from it
// (`rawThumbnailRendered`) — the page's own check before the upload proves the format, not that
// Blender can read the file — and the frames are rendered when both are saved. Setting up the
// studio is counted as a minute (owner: "закладываем, что примерно минуту"; a GPU took 2.5–3.5 min
// on dev, 20.10, the bar then waits near its end); checking and rendering as measured on dev
// (~25 s and ~35 s). No frame is shown before the last one ends (owner, 2026-09-23: a render is
// never instant).
export interface RenderPreviewStage {
  from: number;
  expectedMs: number;
  label: string;
  doneLabel: string;
}

export const RENDER_PREVIEW_STAGES: ReadonlyArray<RenderPreviewStage> = [
  { from: RENDER_UPLOAD_BAR, expectedMs: 60000, label: 'Setting up the scene', doneLabel: 'Scene ready' },
  { from: 30, expectedMs: 25000, label: 'Checking the model', doneLabel: 'Model checked' },
  { from: 50, expectedMs: 35000, label: 'Rendering frames', doneLabel: 'Frames rendered' },
];

// Measured on dev (2026-10-01): about two minutes, up to one of them while a GPU starts.
export const RENDER_PREVIEW_ETA = 'Usually about 2 minutes.';
// The paid videos' time, shown before payment and while they render (Alex, 2026-11-03: "Show the
// expected rendering time before payment" — his test's ready email came about four hours after
// paying). "Usually": an estimate, not a deadline (Refund and Digital Delivery Policy). The render
// emails say the same (the backend's order_paid template).
export const RENDER_FULL_USUAL_HOURS = 4;
export const RENDER_FULL_ETA = `Usually ready in about ${RENDER_FULL_USUAL_HOURS} hours`;
// [IMMEDIATE START CONSENT]: our draft (2026-11-03), not yet confirmed by Alex — replace with his
// words when they come. The request and acknowledgment the Refund Policy promises before an
// immediate start ends an EU withdrawal right; ticked on the order card and the unlock dialog
// before the checkout opens.
export const RENDER_START_CONSENT = 'I ask 3DWay to start rendering right away and acknowledge that I lose my right of withdrawal once the videos are delivered.';

// A preview that comes in on its processing page waits this long before the preview step opens:
// the card finishes first — the bar to its end, the last line ticked (owner, 2026-10-20: no jumps).
export const RENDER_PREVIEW_FINALE_MS = 2000;

export interface RenderFaqItem {
  question: string;
  answer: string;
  // Ends with a link that opens the export guide (render-help-dialog).
  guide?: boolean;
  // About getting the textures in: first in the Help dialog, which opens from the upload step.
  textures?: boolean;
}

// The landing's "A few good questions" and the Help dialog's Questions tab. ZIP only where the
// browser can unpack one (render-zip.ts, canInflate).
export function renderFaq(opensZip: boolean): RenderFaqItem[] {
  return [
    {
      question: `What do I get for $${RENDER_PRICE_USD}?`,
      answer: 'Both portrait and landscape 360° videos of one model. One payment unlocks the pack; you can download the purchased files again without paying again.',
    },
    {
      question: 'Can I see my result before paying?',
      answer: 'Yes. Sign in and preview a frame of your model in both orientations first. No payment card is needed to see it.',
    },
    {
      question: 'Which files can I upload?',
      answer: `FBX and USDZ. Drop a single file or a folder with the model and its textures${opensZip ? ', or a ZIP of either' : ''}. The upload step checks your files before processing.`,
    },
    {
      question: 'Why does my model look white?',
      answer: 'Its textures didn’t come with it. An FBX carries image textures only when they are packed into the file or dropped with it in one folder. Export it again with the textures packed in, or drop the model’s whole folder.',
      guide: true,
      textures: true,
    },
    {
      question: 'How do I include my textures?',
      answer: 'Pack them into the FBX: in Blender, set Path Mode to Copy and turn on the embed button; in Maya and 3ds Max, tick Embed Media. Or drop the folder with the model and its textures.',
      guide: true,
      textures: true,
    },
    {
      question: 'Which texture formats work?',
      answer: 'PNG, JPG, TGA, TIFF and EXR. For transparency, use PNG, TGA or TIFF.',
      textures: true,
    },
    {
      question: 'What if the preview does not look right?',
      answer: 'You do not have to buy it. Check your model and materials, then try again. A failed preview will not take you to checkout.',
      guide: true,
    },
  ];
}

